import Box from "@mui/material/Box";
import NodeSkeleton from "./NodeSkeleton";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React, { memo } from "react";
import { Position, type NodeProps } from "@xyflow/react";
import { useTheme } from "@mui/material/styles";
import CountryFlag from "./CountryFlag";
import type { ExplorerNode } from "../../declarations/IExplorerTypes";
import { splitPascalCase } from "../../utilities/ObjectUtilities";
import { getNodeColor, getNodeIcon } from "./nodeColors";
import NodeListCard from "./NodeListCard";
import NodeToolbar from "./NodeToolbar";

function FMRouteNode({ data }: NodeProps<ExplorerNode>) {
    const theme = useTheme();
    const siteSteps = data.fmSiteSteps ?? [];
    const isLoading = data.status === "loading";
    const isError = data.status === "error";
    const nodeColor = getNodeColor(data.entityType);
    const isDark = theme.palette.mode === "dark";

    return (
        <NodeListCard
            nodeColor={nodeColor}
            isSelected={data.isSelected}
            isLoading={isLoading}
            minWidth={240}
            targetPosition={Position.Top}
            targetHandleId="top"
        >
            <NodeToolbar
                nodeColor={nodeColor}
                icon={getNodeIcon(data.entityType)}
                title={data.nodeTitle}
                subtitle={data.nodeSubtitle}
                global
                onInfoClick={data.selectId ? () => data.onSelect?.(data.selectId!) : undefined}
            />

            <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {data.fmStatusForOutput && (
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>Status</Typography>
                        <Typography variant="caption" fontWeight={500}>{data.fmStatusForOutput}</Typography>
                    </Stack>
                )}
                {data.fmCurrentStagePercentage != null && (
                    <>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>Stage</Typography>
                            <Typography variant="caption" fontWeight={500}>
                                {data.fmCurrentStagePercentage}%
                            </Typography>
                        </Stack>
                        <Box
                            sx={{
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                                overflow: "hidden",
                            }}
                        >
                            <Box
                                sx={{
                                    height: "100%",
                                    width: `${Math.min(Math.max(data.fmCurrentStagePercentage, 0), 100)}%`,
                                    borderRadius: 2,
                                    background: `linear-gradient(90deg, ${nodeColor}cc, ${nodeColor})`,
                                    transition: "width 0.4s ease",
                                }}
                            />
                        </Box>
                    </>
                )}
            </Stack>

            {isLoading ? (
                <NodeSkeleton rows={4} variant="list" />
            ) : isError ? (
                <Typography variant="caption" color="error">
                    Could not load FM manufacturing route
                </Typography>
            ) : siteSteps.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                    No site steps found
                </Typography>
            ) : (
                <List dense disablePadding sx={{ mt: 0.5 }}>
                    {siteSteps.map((step, index) => (
                        <React.Fragment key={`${step.siteTypeId}-${index}`}>
                            {index > 0 && <Divider sx={{ mx: 0.5, opacity: 0.4 }} />}
                            <ListItem dense sx={{ px: 0.5, py: 0.25 }}>
                                <ListItemText
                                    disableTypography
                                    primary={
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5} sx={{ width: "100%" }}>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                {step.countryCode && (
                                                    <CountryFlag countryCode={step.countryCode} size={12} />
                                                )}
                                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3, opacity: 0.8 }}>
                                                    {splitPascalCase(step.siteTypeName)}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="caption" fontWeight={500} sx={{ lineHeight: 1.3 }}>
                                                {step.divisionName ?? step.siteName}
                                            </Typography>
                                        </Stack>
                                    }
                                />
                            </ListItem>
                        </React.Fragment>
                    ))}
                </List>
            )}
        </NodeListCard>
    );
}

export default memo(FMRouteNode);
